// Angular
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
// Material
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
// RXJS
import { debounceTime, distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
// Translate Module
import { TranslateService } from '@ngx-translate/core';
// NGRX
import { select, Store } from '@ngrx/store';
// Services
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
// Models
import {
  AgreementTemplate,
  AgreementTemplateActionTypes,
  AgreementTemplatesDataSource,
  selectAgreementError,
  selectAgreementTemplateLatestSuccessfullAction,
} from '../../../../../core/agreement';
import { AppState } from '../../../../../core/reducers';
// Components
import { AgreementTemplateEditComponent } from '../template-edit/template-edit.component';
// Management components
import { OfficeModel } from 'src/app/core/management';
import {
  AgreementTemplateOnServerDeleted,
  AgreementTemplatesPageRequested,
} from 'src/app/core/agreement/_actions/agreement-template.actions';
import { UserService } from 'src/app/core/auth/_services';
import { AgreementService } from 'src/app/core/agreement/_services';
import { AgreementType } from 'src/app/core/agreement/_models/agreement-type.model';
import { PdfViewDialogComponent } from '../pdf-view/pdf-view';
import { currentUser } from 'src/app/core/auth/_selectors/auth.selectors';
import * as moment from 'moment';
import { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../core/services/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { MY_FORMATS } from '../../../apps/lists/patients/patient-edit/patient-edit.component';

@Component({
  selector: 'kt-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class AgreementTemplateListComponent implements OnInit, OnDestroy {
  dataSource: AgreementTemplatesDataSource;
  offices: OfficeModel[] = [];
  templateTypes: AgreementType[] = [];
  allowedOffices: number[] = [];
  userPrimaryOffice: number;
  displayedColumns = ['id', 'name', 'type', 'created', 'actions'];
  searchBarColumns = ['searchID', 'searchName', 'searchType', 'searchDateCreated', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchIDInput = '';
  searchNameInput = '';
  searchTypeInput = '';
  searchDateCreatedInput: Moment | any;
  @ViewChild('searchDateCreatedPicker') searchDateCreatedPicker: MatDatepicker<Moment>;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private userService: UserService,
    private navigationService: NavigationService,
    private agreementService: AgreementService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    // get the allowed offices for this user.
    this.subscriptions.push(
      this.store.pipe(select(currentUser)).subscribe((res) => {
        this.allowedOffices = JSON.parse(res.otherOffices).otherOffices;
        this.allowedOffices.push(res.primaryOffice);
        this.userPrimaryOffice = res.primaryOffice;
      }),
    );

    this.subscriptions.push(
      this.userService.getAllOffices().subscribe((res) => {
        this.offices = res;
      }),
    );

    this.subscriptions.push(
      this.agreementService.getTypes().subscribe((res) => {
        this.templateTypes = res;
      }),
    );
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push(sortSubscription);

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadAgreementTemplateList();
        }),
      )
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadAgreementTemplateList();
        }),
      )
      .subscribe();
    this.subscriptions.push(searchSubscription);

    this.dataSource = new AgreementTemplatesDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadAgreementTemplateList();
    });
    this.subscriptions.push(routeSubscription);

    this.subscriptions.push(
      this.store.pipe(select(selectAgreementError), skip(1)).subscribe((res) => {
        if (res) {
          this.layoutUtilsService.generalDialog(
            '',
            `
            <div style="height: 200px" class="d-flex flex-column flex-center">
              <i style="font-size: 80px" class="flaticon-warning text-danger"></i>
              <p class="font-weight-bold text-center">${
                res.error?.errors[0]?.message || this.translate.instant('GLOBAL.UNSPECIFIED_ERROR')
              }</p>
            </div>
          `,
            '',
            '',
            'Close',
            false,
            true,
            '400px',
          );
        }
      }),
    );
    this.subscriptions.push(
      this.store.pipe(select(selectAgreementTemplateLatestSuccessfullAction)).subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;

          switch (res.action_type) {
            case AgreementTemplateActionTypes.AgreementTemplateCreated:
              this.reloadAgreementTemplateList();
              successMsg = this.translate.instant('AGREEMENT_TEMPLATE.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case AgreementTemplateActionTypes.AgreementTemplateUpdated:
              this.reloadAgreementTemplateList();
              successMsg = this.translate.instant('AGREEMENT_TEMPLATE.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
            case AgreementTemplateActionTypes.AgreementTemplateActivated:
              this.reloadAgreementTemplateList();
              successMsg = this.translate.instant(
                'AGREEMENT_TEMPLATE.ACTIVATE_AGREEMENT_TEMPLATE.MESSAGE',
              );
              msgType = MessageType.Update;
              break;
          }
          if (successMsg) {
            this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
          }
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadAgreementTemplateList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    // Call request from server
    this.store.dispatch(new AgreementTemplatesPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchIDInput = appliedFilters.id;
        this.searchNameInput = appliedFilters.name;
        this.searchTypeInput = appliedFilters.type;
        if (appliedFilters.dateCreated) {
          this.searchDateCreatedInput = moment(appliedFilters.dateCreated);
        } else {
          this.searchDateCreatedInput = '';
          this.searchDateCreatedPicker?.select(undefined);
        }
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'name';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    const dateCreatedIsMoment = moment.isMoment(this.searchDateCreatedInput);

    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.id = this.searchIDInput;
      filter.name = this.searchNameInput;
      filter.type = this.searchTypeInput;
      filter.dateCreated = this.formatDate(
        dateCreatedIsMoment ? this.searchDateCreatedInput.format('MM-DD-YYYY') : '',
      );
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'name';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  formatDate(date: string): string {
    if (!date) return null;

    const dateList = date.split('-');

    dateList.unshift(dateList.pop());

    return dateList.join('-');
  }

  addAgreementTemplate() {
    const newTemplate = new AgreementTemplate();
    newTemplate.clear(); // Set all defaults fields
    this.editTemplate(newTemplate, false);
  }

  reloadAgreementTemplateList(): void {
    this.paginator.pageIndex = 0;

    this.loadAgreementTemplateList();
  }

  viewTemplate(template: AgreementTemplate) {
    this.agreementService.getTemplate(template.id).subscribe((res) => {
      const dialogConfig = new MatDialogConfig();

      dialogConfig.closeOnNavigation = true;
      dialogConfig.height = '80vh';
      dialogConfig.maxHeight = '80vh';
      dialogConfig.width = '75vh';
      dialogConfig.maxWidth = '75vh';
      dialogConfig.data = { url: res.url };

      const dialogRef = this.dialog.open(PdfViewDialogComponent, dialogConfig);

      dialogRef.afterClosed().subscribe();
    });
  }

  editTemplate(template: AgreementTemplate, isEdit: boolean) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.maxHeight = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.width = '100vw';

    const ref = this.dialog.open(AgreementTemplateEditComponent, {
      data: {
        offices: this.offices.filter((o) => this.allowedOffices.includes(o.id)), // Only include the offices that this user is allowed to create templates for.
        userPrimaryOfficeId: this.userPrimaryOffice,
        template: template,
        templateTypes: this.templateTypes,
        isEdit: isEdit,
      },
    });

    ref.afterClosed().subscribe((res) => {
      if (res) {
        this.layoutUtilsService.showActionNotification(
          this.translate.instant('AGREEMENT_TEMPLATE.EDIT.UPDATE_MESSAGE'),
          MessageType.Update,
          3000,
          true,
          false,
        );
        this.reloadAgreementTemplateList();
      }
    });
  }

  deleteTemplate(template: AgreementTemplate) {
    const _title: string = this.translate.instant(
      'AGREEMENT_TEMPLATE.DELETE_AGREEMENT_TEMPLATE_SIMPLE.TITLE',
    );
    const _description: string = this.translate.instant(
      'AGREEMENT_TEMPLATE.DELETE_AGREEMENT_TEMPLATE_SIMPLE.DESCRIPTION',
    );
    const _waitDescription: string = this.translate.instant(
      'AGREEMENT_TEMPLATE.DELETE_AGREEMENT_TEMPLATE_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(
        new AgreementTemplateOnServerDeleted({
          id: template.id,
          office_id: this.userPrimaryOffice,
        }),
      );

      this.dataSource.filter((item) => item.id != template.id);

      this.layoutUtilsService.showActionNotification(
        this.translate.instant('AGREEMENT_TEMPLATE.DELETE_AGREEMENT_TEMPLATE_SIMPLE.MESSAGE'),
        MessageType.Delete,
        3000,
        true,
        false,
      );
    });
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchIDInput = '';
    this.searchNameInput = '';
    this.searchTypeInput = '';
    this.searchDateCreatedInput = '';
    this.searchDateCreatedPicker?.select(undefined);
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadAgreementTemplateList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadAgreementTemplateList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadAgreementTemplateList();
  }
}
